<template>
  <v-dialog
    :value="true"
    persistent
    scrollable
    max-width="720"
    content-class="ext-edit-dialog"
  >
    <v-card class="ext-edit-card">
      <!-- スクロールしても内容と合計・印刷導線を見失わないようヘッダーは固定 -->
      <div class="ext-edit-header">
        <div class="ext-edit-header__main">
          <div class="ext-edit-title">
            {{ courseName }}
          </div>

          <div class="ext-edit-total">
            <v-progress-circular
              v-if="isCalculating"
              indeterminate
              size="18"
              width="2"
            />
            <template v-else-if="estimatedTotal !== null">
              <span class="ext-edit-total__amount">¥{{ Number(estimatedTotal).toLocaleString() }}</span>
              <span class="ext-edit-total__label">注文後のお会計</span>
            </template>
          </div>

          <v-btn
            icon
            :ripple="false"
            aria-label="閉じる"
            class="ext-edit-close"
            @click="$emit('close')"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </div>

        <div class="ext-edit-header__actions">
          <print-btn
            subject="概算伝票"
            :print="printEstimatedSlip"
          >
            <template #append>
              <preview :slip-svg="slipSvg" />
            </template>
          </print-btn>
        </div>
      </div>

      <div class="ext-edit-body">
        <div
          v-for="line in lines"
          :key="line.key"
          class="ext-edit-row"
          :class="{ 'is-removed': line.removed }"
        >
          <!-- 数値系コントロールと削除を隣り合わせにしないため、削除は行の右端に縦一列で分離する -->
          <div class="ext-edit-row__body">
            <div class="ext-edit-row__head">
              <div class="ext-edit-row__label">
                {{ line.label }}
              </div>

              <v-select
                v-if="line.vendibleOptions"
                :value="line.temp.value.vendibleId"
                :items="line.vendibleOptions"
                item-text="name"
                item-value="id"
                dense
                hide-details
                :disabled="line.removed"
                class="ext-edit-vendible-select"
                :aria-label="`${line.label}の商品`"
                @change="changeVendible(line.temp, $event, line.vendibleOptions)"
              />
            </div>

            <div class="ext-edit-row__controls">
              <v-text-field
                :value="line.temp.value.sellingPrice"
                type="number"
                dense
                hide-details
                prefix="¥"
                :disabled="line.removed"
                class="ext-edit-price-field"
                :aria-label="`${line.label}の単価`"
                @input="changeSellingPrice(line.temp, $event)"
              />

              <div class="ext-edit-stepper">
                <v-btn
                  icon
                  :ripple="false"
                  :disabled="line.removed || line.temp.size <= 1"
                  class="ext-edit-stepper__btn"
                  :aria-label="`${line.label}の数量を減らす`"
                  @click="changeSize(line.temp, line.temp.size - 1)"
                >
                  <v-icon small>
                    {{ icons.mdiMinus }}
                  </v-icon>
                </v-btn>

                <span class="ext-edit-stepper__value">{{ line.temp.size }}</span>

                <v-btn
                  icon
                  :ripple="false"
                  :disabled="line.removed"
                  class="ext-edit-stepper__btn"
                  :aria-label="`${line.label}の数量を増やす`"
                  @click="changeSize(line.temp, line.temp.size + 1)"
                >
                  <v-icon small>
                    {{ icons.mdiPlus }}
                  </v-icon>
                </v-btn>
              </div>

              <v-btn-toggle
                :value="line.temp.value.taxCharge"
                mandatory
                dense
                :disabled="line.removed"
                class="ext-edit-tax-toggle"
                :aria-label="`${line.label}の料金区分`"
                @change="changeTaxCharge(line.temp, $event)"
              >
                <v-btn
                  v-for="taxOption in taxOptions"
                  :key="taxOption.value"
                  :value="taxOption.value"
                  small
                  :ripple="false"
                  :disabled="isTaxOptionDisabled(taxOption)"
                  :title="isTaxOptionDisabled(taxOption) ? includedUnavailableHint : null"
                  :aria-label="taxOption.label"
                >
                  {{ taxOption.label }}
                </v-btn>
              </v-btn-toggle>
            </div>

            <v-select
              v-if="line.kind === 'nomination'"
              :value="recipientUserIds(line.temp)"
              :items="castOptions"
              item-text="name"
              item-value="id"
              multiple
              dense
              hide-details
              :disabled="line.removed"
              placeholder="受領キャスト"
              class="ext-edit-recipient-select mt-3"
              :aria-label="`${line.label}の受領キャスト`"
              @change="changeRecipients(line.temp, $event)"
            />

            <!-- 指名は受領キャストが決まらないとバックが付かないため、未選択のまま注文させない -->
            <div
              v-if="line.recipientMissing"
              class="ext-edit-row__alert"
            >
              受領キャストを選択してください
            </div>

            <!-- 既に込みが選ばれている行は勝手に書き換えず、送信前に気づけるよう警告だけ出す -->
            <div
              v-if="line.contributorMissing"
              class="ext-edit-row__alert"
            >
              計上先がいないため、このままでは延長できません
            </div>

            <div class="ext-edit-row__subtotal">
              <span
                v-if="line.removed"
                class="ext-edit-row__removed-note"
              >
                削除予定
              </span>
              小計 ¥{{ lineSubtotal(line.temp).toLocaleString() }}
            </div>
          </div>

          <div class="ext-edit-row__side">
            <v-btn
              v-if="line.removable"
              icon
              :ripple="false"
              class="ext-edit-row__remove"
              :aria-label="line.removed ? `${line.label}の削除を取り消す` : `${line.label}を削除`"
              @click="toggleRemove(line.temp)"
            >
              <v-icon small>
                {{ line.removed ? icons.mdiUndoVariant : icons.mdiTrashCanOutline }}
              </v-icon>
            </v-btn>
          </div>
        </div>

        <div class="ext-edit-add">
          <v-btn
            text
            :ripple="false"
            :disabled="nominationOptions.length === 0"
            :aria-label="`${nominationAlias}を追加`"
            @click="addNomination"
          >
            <v-icon
              small
              left
            >
              {{ icons.mdiPlus }}
            </v-icon>
            {{ nominationAlias }}を追加
          </v-btn>

          <v-btn
            text
            :ripple="false"
            :disabled="itemOptions.length === 0"
            :aria-label="`${itemAlias}を追加`"
            @click="addItem"
          >
            <v-icon
              small
              left
            >
              {{ icons.mdiPlus }}
            </v-icon>
            {{ itemAlias }}を追加
          </v-btn>
        </div>
      </div>

      <div class="ext-edit-footer">
        <v-btn
          :ripple="false"
          class="ext-edit-footer__btn"
          :loading="isHolding"
          :disabled="isBusy || hasMissingRecipient"
          aria-label="次の延長に設定"
          @click="hold"
        >
          次の延長に設定
        </v-btn>

        <v-btn
          color="primary"
          :ripple="false"
          class="ext-edit-footer__btn"
          :loading="isExtending"
          :disabled="isBusy || disabled || hasMissingRecipient"
          aria-label="即延長"
          @click="extendNow"
        >
          即延長
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  inject,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  cloneDeep,
  compact,
  filter,
  find,
  first,
  includes,
  isEmpty,
  map,
  reject,
  some,
  without,
} from 'lodash'
import {
  mdiClose,
  mdiMinus,
  mdiPlus,
  mdiTrashCanOutline,
  mdiUndoVariant,
} from '@mdi/js'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import ExtensionPlanApi from '@/api/waiter/ExtensionPlan'
import useCurrentData from '@/views/composable/useCurrentData'
import useVendingHistory from '@/views/composable/useVendingHistory'
import useExtensionEstimation, { getTotalTax } from '@/views/composable/useExtensionEstimation'
import useExtensionSlipPrint from '@/views/composable/useExtensionSlipPrint'
import { buildExtensionPlanPayload } from '@/views/composable/useExtensionPlan'
import { pickApiErrorMessage } from '@/utils/apiErrorMessage'
import PrintBtn from '@/views/components/util/PrintBtn.vue'
import Preview from '@/views/components/printer/Preview.vue'

const INCLUDED_UNAVAILABLE_HINT = '売上の計上先となる指名がないため込みは選べません'

const toVendibleOptions = vendibles => {
  return map(vendibles, vendible => ({
    id: Number(vendible.id),
    name: vendible.attributes?.name,
    sellingPrice: vendible.attributes?.sellingPrice,
  }))
}

export default {
  components: {
    PrintBtn,
    Preview,
  },
  props: {
    // NOTE: { course, nominations, items } のvendingHistoryTemp束。候補カード・保持プランのどちらからも同じ形で渡す
    seed: {
      type: Object,
      required: true,
    },
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    customName: {
      type: String,
      default: '',
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    total: {
      type: [Number, String],
      required: true,
    },
    paidInAdvanceAmount: {
      type: [Number, String],
      default: 0,
    },
    totalDepositAmount: {
      type: [Number, String],
      default: 0,
    },
    // 自動延長の発火窓など、注文自体を受け付けない状態（設定の保存は可能）
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const courses = inject('courses', ref([]))
    const nominations = inject('nominations', ref([]))
    const items = inject('items', ref([]))
    const users = inject('users', ref([]))

    const { currentClub } = useCurrentData()
    const {
      buildVendingHistoryTemp,
      buildVendingHistoriesToSubmit,
      setVendingContributionsAttributes,
      resolveVendingContributorIds,
    } = useVendingHistory()

    const courseTemp = reactive(cloneDeep(props.seed.course))
    const nominationTemps = ref(map(props.seed.nominations || [], temp => reactive(cloneDeep(temp))))
    const itemTemps = ref(map(props.seed.items || [], temp => reactive(cloneDeep(temp))))

    const isHolding = ref(false)
    const isExtending = ref(false)
    const isBusy = computed(() => isHolding.value || isExtending.value)

    // NOTE: 削除は即時に行を消さず「削除予定」として印を付け、保存（保持・即延長）時にまとめて反映する。
    // タブレットでの誤タップを取り消せるようにするため
    const removedTemps = ref([])
    const isRemoved = temp => includes(removedTemps.value, temp)

    const toggleRemove = temp => {
      removedTemps.value = isRemoved(temp) ? without(removedTemps.value, temp) : [...removedTemps.value, temp]
    }

    const activeNominationTemps = computed(() => reject(nominationTemps.value, isRemoved))
    const activeItemTemps = computed(() => reject(itemTemps.value, isRemoved))

    const vendingHistoriesTemp = computed(() => {
      return compact([courseTemp, ...activeNominationTemps.value, ...activeItemTemps.value])
    })

    // NOTE: 「込み」は税を負担する売上計上先が必要。計上先の導出は自動セットと同じ関数に寄せる
    const hasNoContributor = computed(() => {
      return isEmpty(resolveVendingContributorIds({ targetVendingHistoriesTemp: vendingHistoriesTemp.value }))
    })

    const isTaxOptionDisabled = taxOption => taxOption.value === 'included' && hasNoContributor.value

    const { estimatedTotal, isCalculating } = useExtensionEstimation({
      tableId: () => props.tableId,
      vendingHistoriesTemp,
    })

    const courseName = computed(() => {
      const targetCourse = find(courses.value, course => Number(course.id) === Number(courseTemp.value.vendibleId))

      return targetCourse?.attributes?.name || '延長セット'
    })

    const courseOptions = computed(() => toVendibleOptions(courses.value))
    const nominationOptions = computed(() => toVendibleOptions(nominations.value))
    const itemOptions = computed(() => toVendibleOptions(items.value))

    const nominationAlias = computed(() => currentClub.value?.nominationAlias || '指名')
    const itemAlias = computed(() => currentClub.value?.itemAlias || 'アイテム')

    const castOptions = computed(() => {
      return map(
        filter(users.value, user => user.attributes?.role === 'cast'),
        user => ({ id: Number(user.id), name: user.attributes?.name }),
      )
    })

    // NOTE: 指名は受領キャストが決まって初めてバックが付く。削除予定の行は注文に含めないので対象外
    const isRecipientMissing = temp => !isRemoved(temp) && isEmpty(temp.value.vendibleRecipientsAttributes)

    const hasMissingRecipient = computed(() => some(nominationTemps.value, isRecipientMissing))

    // NOTE: 削除予定の行は注文に含めないので警告の対象外
    const isContributorMissing = temp => {
      return !isRemoved(temp) && temp.value.taxCharge === 'included' && hasNoContributor.value
    }

    const lines = computed(() => {
      return [
        {
          key: 'course',
          kind: 'course',
          temp: courseTemp,
          label: `${currentClub.value?.courseAlias || 'セット'}料金`,
          removable: false,
          removed: false,
          recipientMissing: false,
          contributorMissing: isContributorMissing(courseTemp),
          vendibleOptions: courseOptions.value,
        },
        ...map(nominationTemps.value, (temp, index) => ({
          key: `nomination-${index}`,
          kind: 'nomination',
          temp,
          label: nominationAlias.value,
          removable: true,
          removed: isRemoved(temp),
          recipientMissing: isRecipientMissing(temp),
          contributorMissing: isContributorMissing(temp),
          vendibleOptions: nominationOptions.value,
        })),
        ...map(itemTemps.value, (temp, index) => ({
          key: `item-${index}`,
          kind: 'item',
          temp,
          label: itemAlias.value,
          removable: true,
          removed: isRemoved(temp),
          recipientMissing: false,
          contributorMissing: isContributorMissing(temp),
          vendibleOptions: itemOptions.value,
        })),
      ]
    })

    const lineSubtotal = temp => (Number(temp.value.sellingPrice) || 0) * temp.size

    const changeSellingPrice = (temp, value) => {
      // eslint-disable-next-line no-param-reassign
      temp.value.sellingPrice = value === '' ? 0 : Number(value)
    }

    const changeSize = (temp, size) => {
      if (size < 1) return

      // eslint-disable-next-line no-param-reassign
      temp.size = size
    }

    const changeTaxCharge = (temp, taxCharge) => {
      // eslint-disable-next-line no-param-reassign
      temp.value.taxCharge = taxCharge
    }

    // NOTE: 差し替え時は差し替え先の定価に戻す（前の商品の金額が残ると事故になる）
    const changeVendible = (temp, vendibleId, vendibleOptions) => {
      const target = find(vendibleOptions, option => Number(option.id) === Number(vendibleId))
      if (!target) return

      // eslint-disable-next-line no-param-reassign
      temp.value.vendibleId = target.id
      // eslint-disable-next-line no-param-reassign
      temp.value.sellingPrice = Number(target.sellingPrice) || 0
    }

    // NOTE: 延長にその場で指名を足せるようにする（旧UIの延長候補カードと同じ編集力）。
    // 初期値は先頭の指名の定価・既定税区分。差し替えは既存の商品セレクトで行う
    const addNomination = () => {
      const targetNomination = first(nominations.value)
      if (!targetNomination) return

      nominationTemps.value = [
        ...nominationTemps.value,
        reactive(buildVendingHistoryTemp({
          vendibleId: Number(targetNomination.id),
          vendibleType: 'Nomination',
          sellingPrice: Number(targetNomination.attributes?.sellingPrice) || 0,
          taxCharge: targetNomination.attributes?.defaultTaxCharge || 'normal',
        })),
      ]
    }

    // NOTE: 指名と同じく、延長にその場でアイテムを足せるようにする。初期値は先頭のアイテムの定価・既定税区分
    const addItem = () => {
      const targetItem = first(items.value)
      if (!targetItem) return

      itemTemps.value = [
        ...itemTemps.value,
        reactive(buildVendingHistoryTemp({
          vendibleId: Number(targetItem.id),
          vendibleType: 'Item',
          sellingPrice: Number(targetItem.attributes?.sellingPrice) || 0,
          taxCharge: targetItem.attributes?.defaultTaxCharge || 'normal',
        })),
      ]
    }

    const recipientUserIds = temp => {
      return map(temp.value.vendibleRecipientsAttributes || [], recipient => Number(recipient.userId))
    }

    // NOTE: 受領キャストが複数のときの分け前はUI上は等分に固定する（個別比率は注文画面で調整する運用）
    const changeRecipients = (temp, userIds) => {
      const ratio = userIds.length > 0 ? 1.0 / userIds.length : 0

      // eslint-disable-next-line no-param-reassign
      temp.value.vendibleRecipientsAttributes = map(userIds, userId => ({ userId: Number(userId), ratio }))
    }

    // NOTE: 受領キャストの変更に追従して売上計上先を振り直す（候補カードと同じ挙動）
    watch(() => cloneDeep(vendingHistoriesTemp.value), () => {
      setVendingContributionsAttributes({ targetVendingHistoriesTemp: vendingHistoriesTemp.value })
    })

    // 概算伝票は「編集後の金額」で出す（割引後の金額を提示する運用があるため）
    const extensionCandidates = computed(() => [{
      courseHistoryTemp: courseTemp,
      nominationHistoriesTemp: activeNominationTemps.value,
      itemHistoriesTemp: activeItemTemps.value,
      newHistoriesTemp: [],
    }])

    const estimatedTotals = computed(() => {
      if (estimatedTotal.value === null) return []

      return [{ extensionCandidateIdx: 0, amount: estimatedTotal.value }]
    })

    const { slipSvg, printEstimatedSlip } = useExtensionSlipPrint({
      props,
      extensionCandidates,
      estimatedTotals,
      getTotalTax,
    })

    const buildPlan = () => buildExtensionPlanPayload({
      courseTemp,
      nominationTemps: activeNominationTemps.value,
      itemTemps: activeItemTemps.value,
    })

    const hold = async () => {
      if (isBusy.value) return
      if (hasMissingRecipient.value) return

      isHolding.value = true

      try {
        const res = await ExtensionPlanApi.updateExtensionPlan({
          tableId: props.tableId,
          extensionPlan: buildPlan(),
        })

        if (res?.status === 200) {
          vm.$toast.success('次の延長に設定しました')
          emit('held', { table: res.data.table.data })
          emit('close')
        } else {
          vm.$toast.error('設定に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('設定に失敗しました')
      } finally {
        isHolding.value = false
      }
    }

    const extendNow = async () => {
      if (isBusy.value) return
      if (props.disabled) return
      if (hasMissingRecipient.value) return

      isExtending.value = true

      // NOTE: シードのまま無編集で押されるとwatchが一度も走らず売上計上先が付かない。
      // 付いていないと税区分「込み」の明細が弾かれるため送信直前に付け直す
      setVendingContributionsAttributes({ targetVendingHistoriesTemp: vendingHistoriesTemp.value })

      try {
        const res = await VendingHistoryApi.createVendingHistories({
          tableId: props.tableId,
          vendingHistories: buildVendingHistoriesToSubmit(vendingHistoriesTemp.value),
        })

        if (res?.status === 200) {
          // NOTE: プランは消費しない（卓が終わるまで有効）。2回目以降の延長・自動延長にも同じ内容が使われる
          emit('extended', res.data)
          emit('close')
        } else {
          vm.$toast.error('延長に失敗しました')
        }
      } catch (e) {
        // NOTE: 422はバックエンドが理由をJSONで返すので、原因が分かるようそのまま出す
        vm.$toast.error(pickApiErrorMessage(e) || '延長に失敗しました')
      } finally {
        isExtending.value = false
      }
    }

    return {
      courseName,
      lines,
      castOptions,
      nominationOptions,
      itemOptions,
      nominationAlias,
      itemAlias,
      hasMissingRecipient,
      hasNoContributor,
      isTaxOptionDisabled,
      includedUnavailableHint: INCLUDED_UNAVAILABLE_HINT,
      estimatedTotal,
      isCalculating,
      isHolding,
      isExtending,
      isBusy,
      slipSvg,

      lineSubtotal,
      recipientUserIds,
      changeSellingPrice,
      changeSize,
      changeTaxCharge,
      changeVendible,
      changeRecipients,
      addNomination,
      addItem,
      toggleRemove,
      printEstimatedSlip,
      hold,
      extendNow,

      taxOptions: [
        { value: 'normal', label: '通常' },
        { value: 'included', label: '込み' },
        { value: 'cut', label: 'カット' },
      ],
      icons: {
        mdiClose,
        mdiMinus,
        mdiPlus,
        mdiTrashCanOutline,
        mdiUndoVariant,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.ext-edit-card {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  font-size: 14px;
}

.ext-edit-header {
  flex: 0 0 auto;
  padding: 12px 16px 8px;
  border-bottom: 1px solid var(--overview-border, rgba(28, 28, 46, 0.1));
}

.ext-edit-header__main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ext-edit-title {
  flex: 1;
  min-width: 0;
  font-size: 18px;
  font-weight: 700;
}

.ext-edit-total {
  text-align: right;
  line-height: 1.2;
}

.ext-edit-total__amount {
  display: block;
  font-size: 19px;
  font-weight: 800;
}

.ext-edit-total__label {
  display: block;
  font-size: 11px;
  opacity: 0.6;
}

.ext-edit-close {
  flex: 0 0 auto;
}

.ext-edit-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.ext-edit-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 16px 12px;
}

// タブレットでの誤タップ対策として、行同士・行内コントロールの間隔を広めに取る
.ext-edit-row {
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid var(--overview-border, rgba(28, 28, 46, 0.1));

  // 削除予定の行は取り消し線で示すだけに留め、保存するまで復元できるようにする
  &.is-removed .ext-edit-row__body {
    opacity: 0.5;
    text-decoration: line-through;
  }
}

.ext-edit-row__body {
  flex: 1;
  min-width: 0;
}

// 削除は数値系コントロールから離し、行の右端に独立した領域として置く
.ext-edit-row__side {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  padding-left: 12px;
  border-left: 1px solid var(--overview-border, rgba(28, 28, 46, 0.1));
}

.ext-edit-row__head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ext-edit-row__label {
  font-weight: 700;
  white-space: nowrap;
}

.ext-edit-vendible-select {
  flex: 1;
  min-width: 0;
}

.ext-edit-row__remove {
  width: 44px;
  height: 44px;
}

.ext-edit-row__controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 24px;
  margin-top: 12px;
}

.ext-edit-price-field {
  max-width: 140px;
}

.ext-edit-stepper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ext-edit-stepper__btn {
  width: 44px;
  height: 44px;
}

.ext-edit-stepper__value {
  min-width: 32px;
  text-align: center;
  font-weight: 700;
}

// タブレット運用での視認性優先（候補カードと揃える）
.ext-edit-tax-toggle .v-btn {
  font-size: 13px;
  height: 44px;
}

.ext-edit-row__alert {
  margin-top: 6px;
  font-size: 12px;
  color: var(--overview-rose, #e11d48);
}

.ext-edit-row__subtotal {
  margin-top: 12px;
  text-align: right;
  font-weight: 700;
}

.ext-edit-row__removed-note {
  margin-right: 8px;
  font-size: 12px;
  font-weight: 400;
}

.ext-edit-add {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
  padding-top: 12px;
}

.ext-edit-footer {
  flex: 0 0 auto;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid var(--overview-border, rgba(28, 28, 46, 0.1));
}

.ext-edit-footer__btn {
  flex: 1;
  height: 44px;
}
</style>
